import { config } from '../../../../stateManagement/redux/configVar';

const serverUrl = config.serverUrl;

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = localStorage.getItem('authToken');
  if (!token) throw new Error('No token found');
  return { authorization: token, ...extra };
}

function jsonAuthHeaders(): Record<string, string> {
  return authHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' });
}

export interface UploadDynSupportParams {
  file: File;
  executedAt: string;
  locationIds: string[];
  wellNames?: string[];
  /** Nombre lógico para el almacenamiento (p.ej. "1012_archivo.tam"). Si no se envía, usa file.name. */
  fileName?: string;
}

export type DynSupportRegisterStatus = 'created' | 'linked' | 'duplicated';

export interface DynSupportRegisterResult {
  id?: string;
  status?: DynSupportRegisterStatus;
  message?: string;
  [key: string]: unknown;
}

interface PresignDynSupportResult {
  uploadUrl: string;
  fileKey: string;
  contentType: string;
}

async function presignDynSupportUpload(input: {
  fileName: string;
  executedAt: string;
  contentType: string;
}): Promise<PresignDynSupportResult> {
  const response = await fetch(`${serverUrl}/api/tam-viewer/dyn-support/presign`, {
    method: 'POST',
    headers: jsonAuthHeaders(),
    body: JSON.stringify({
      fileName: input.fileName,
      executedAt: input.executedAt,
      contentType: input.contentType,
      type: 'TAM',
    }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Error al preparar subida (${response.status})`);
  }
  return response.json();
}

function parseApiErrorMessage(raw: string, fallback: string): string {
  const text = (raw || '').trim();
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text) as { message?: string | string[]; error?: string };
    if (Array.isArray(parsed.message)) {
      return parsed.message.filter(Boolean).join(' · ') || fallback;
    }
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message.trim();
    }
    if (typeof parsed.error === 'string' && parsed.error.trim()) {
      return parsed.error.trim();
    }
  } catch {
    /* plain text */
  }
  return text;
}

async function registerDynSupportUpload(input: {
  fileKey: string;
  fileName: string;
  executedAt: string;
  locationIds: string[];
  wellNames?: string[];
}): Promise<DynSupportRegisterResult> {
  const response = await fetch(`${serverUrl}/api/tam-viewer/dyn-support/register`, {
    method: 'POST',
    headers: jsonAuthHeaders(),
    body: JSON.stringify({
      fileKey: input.fileKey,
      fileName: input.fileName,
      executedAt: input.executedAt,
      locationIds: input.locationIds,
      type: 'TAM',
      metadata: input.wellNames?.length
        ? JSON.stringify({ wellNames: input.wellNames })
        : undefined,
    }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      parseApiErrorMessage(text, `Error al registrar TAM (${response.status})`),
    );
  }
  return response.json();
}

export interface DynSupportLocationFile {
  id: string;
  type: string;
  fileName: string;
  executedAt: string;
  wellNames: string[];
  locations: { id: string; name: string }[];
  createdAt: string;
}

export interface DynSupportDailyCount {
  date: string;
  count: number;
}

export interface DynSupportLibrarySummary {
  total: number;
  dailyCounts: DynSupportDailyCount[];
}

/** Resumen por rango: conteos diarios (pestaña Librería). */
export async function fetchDynSupportLibrarySummary(params: {
  fromDate: string;
  toDate: string;
  type?: string;
}): Promise<DynSupportLibrarySummary> {
  const qs = new URLSearchParams({
    fromDate: params.fromDate,
    toDate: params.toDate,
    type: params.type ?? 'TAM',
  });
  const response = await fetch(
    `${serverUrl}/api/tam-viewer/dyn-support/library-summary?${qs}`,
    { method: 'GET', headers: jsonAuthHeaders() },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Error al cargar librería TAM (${response.status})`);
  }
  return response.json();
}

/** Archivos de un día concreto. */
export async function fetchDynSupportLibraryDay(
  date: string,
  type = 'TAM',
): Promise<DynSupportLocationFile[]> {
  const qs = new URLSearchParams({ date, type });
  const response = await fetch(
    `${serverUrl}/api/tam-viewer/dyn-support/library-day?${qs}`,
    { method: 'GET', headers: jsonAuthHeaders() },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Error al cargar archivos del día (${response.status})`);
  }
  return response.json();
}

/** Descarga un TAM al disco del usuario. */
export async function downloadDynSupportFile(fileId: string): Promise<void> {
  const { signedUrl, fileName } = await getDynSupportDownloadUrl(fileId);
  const link = document.createElement('a');
  link.href = signedUrl;
  link.download = fileName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/** Lista los archivos de soporte (TAM por defecto) asociados a un pozo. */
export async function listDynSupportByLocation(
  locationId: string,
  type = 'TAM',
): Promise<DynSupportLocationFile[]> {
  const response = await fetch(
    `${serverUrl}/api/tam-viewer/dyn-support/by-location/${encodeURIComponent(locationId)}?type=${encodeURIComponent(type)}`,
    { method: 'GET', headers: jsonAuthHeaders() },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Error al listar archivos TAM (${response.status})`);
  }
  return response.json();
}

/** Obtiene una URL firmada para descargar un archivo de soporte. */
export async function getDynSupportDownloadUrl(
  fileId: string,
): Promise<{ signedUrl: string; fileName: string }> {
  const response = await fetch(
    `${serverUrl}/api/tam-viewer/dyn-support/${encodeURIComponent(fileId)}/download`,
    { method: 'GET', headers: jsonAuthHeaders() },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Error al generar descarga (${response.status})`);
  }
  return response.json();
}

export async function uploadDynSupportFile(
  params: UploadDynSupportParams,
): Promise<DynSupportRegisterResult> {
  const contentType = params.file.type || 'application/octet-stream';
  const effectiveFileName = params.fileName?.trim() || params.file.name;

  const presign = await presignDynSupportUpload({
    fileName: effectiveFileName,
    executedAt: params.executedAt,
    contentType,
  });

  const s3Res = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': presign.contentType },
    body: params.file,
  });
  if (!s3Res.ok) {
    throw new Error(`No se pudo subir el archivo a almacenamiento (${s3Res.status})`);
  }

  return registerDynSupportUpload({
    fileKey: presign.fileKey,
    fileName: effectiveFileName,
    executedAt: params.executedAt,
    locationIds: params.locationIds,
    wellNames: params.wellNames,
  });
}
